import { fitnessLocalWorkoutId } from './fitnessLocalWorkoutDomain.ts';

export const FITNESS_CURRENT_SET_EDIT_TOOL = 'propose_fitness_current_set_edit';
export interface FitnessCurrentSetEditChanges { weightKg?: number; reps?: number }
export interface FitnessCurrentSetEditState {
  runtimeId: string; revision: number; paused: boolean; busy: boolean; phase: 'execution';
  workoutName: string | null; exerciseName: string; currentExerciseIndex: number; currentSet: number;
  timed: false; done: false; weightKg: number; reps: number;
}
export interface FitnessCurrentSetEditEnvelope {
  version: 1; owner: string; observedAt: string; status: 'available' | 'absent' | 'invalid' | 'unavailable'; set: FitnessCurrentSetEditState | null;
}
export interface FitnessCurrentSetEditArgs { action: 'edit_current_set'; snapshot: FitnessCurrentSetEditEnvelope; changes: FitnessCurrentSetEditChanges }
const fail = (): never => { throw new Error('invalid_fitness_current_set_edit'); };
const object = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  if (prototype === null) return true;
  const constructor = Object.getOwnPropertyDescriptor(prototype, 'constructor');
  if (Object.getPrototypeOf(prototype) !== null || !constructor || !('value' in constructor) || typeof constructor.value !== 'function'
    || Function.prototype.toString.call(constructor.value) !== Function.prototype.toString.call(Object)) return false;
  const nativePrototype = Object.getOwnPropertyDescriptor(constructor.value, 'prototype');
  return !!nativePrototype && 'value' in nativePrototype && nativePrototype.value === prototype;
};
/** Own enumerable data keys only: a preview never carries inherited or hidden authority. */
const dataKeys = (value: Record<string, unknown>, allowed: readonly string[], exact = true) => {
  const keys = Reflect.ownKeys(value);
  return keys.length > 0 && (!exact || keys.length === allowed.length) && keys.every(key => {
    if (typeof key !== 'string' || !allowed.includes(key)) return false;
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    return !!descriptor && descriptor.enumerable === true && 'value' in descriptor;
  });
};
const integer = (value: unknown, min: number, max: number): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max;
const weight = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 10000;
const name = (value: unknown): value is string => typeof value === 'string' && value.length > 0 && value.length <= 200 && value === value.trim() && !/[\p{Cc}\p{Cf}\p{Cs}]/u.test(value);

export function canonicalFitnessCurrentSetEditChanges(input: unknown): FitnessCurrentSetEditChanges {
  if (!object(input) || !dataKeys(input, ['weightKg', 'reps'], false)) return fail();
  const result: FitnessCurrentSetEditChanges = {};
  if (Object.prototype.hasOwnProperty.call(input, 'weightKg')) {
    if (!weight(input.weightKg) || Number(input.weightKg.toFixed(2)) !== input.weightKg) return fail();
    result.weightKg = input.weightKg === 0 ? 0 : input.weightKg;
  }
  if (Object.prototype.hasOwnProperty.call(input, 'reps')) {
    if (!integer(input.reps, 1, 100000)) return fail();
    result.reps = input.reps;
  }
  return result;
}

export function fitnessCurrentSetEditAlreadyMatches(changes: FitnessCurrentSetEditChanges, set: Pick<FitnessCurrentSetEditState, 'weightKg' | 'reps'>): boolean {
  const values = canonicalFitnessCurrentSetEditChanges(changes);
  return (values.weightKg === undefined || values.weightKg === set.weightKg) && (values.reps === undefined || values.reps === set.reps);
}

/** Only the current editor's public numbers and label; no saved draft or private runtime state. */
export function canonicalFitnessCurrentSetEditEnvelope(input: unknown, owner: string, now: Date = new Date()): FitnessCurrentSetEditEnvelope {
  if (!fitnessLocalWorkoutId(owner) || !Number.isFinite(now.getTime()) || !object(input) || !dataKeys(input, ['version', 'owner', 'observedAt', 'status', 'set']) || input.version !== 1 || input.owner !== owner) return fail();
  if (typeof input.observedAt !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(input.observedAt)) return fail();
  const observed = Date.parse(input.observedAt);
  if (!Number.isFinite(observed) || new Date(observed).toISOString() !== input.observedAt || now.getTime() - observed > 300000 || observed - now.getTime() > 30000) return fail();
  if (typeof input.status !== 'string' || !['available', 'absent', 'invalid', 'unavailable'].includes(input.status)) return fail();
  const status = input.status as FitnessCurrentSetEditEnvelope['status'];
  if (status !== 'available') {
    if (input.set !== null) return fail();
    return { version: 1, owner, observedAt: input.observedAt, status, set: null };
  }
  const set = input.set;
  if (!object(set) || !dataKeys(set, ['runtimeId', 'revision', 'paused', 'busy', 'phase', 'workoutName', 'exerciseName', 'currentExerciseIndex', 'currentSet', 'timed', 'done', 'weightKg', 'reps'])
    || !fitnessLocalWorkoutId(set.runtimeId) || !integer(set.revision, 0, Number.MAX_SAFE_INTEGER) || typeof set.paused !== 'boolean' || typeof set.busy !== 'boolean'
    || set.phase !== 'execution' || set.workoutName !== null && !name(set.workoutName) || !name(set.exerciseName)
    || !integer(set.currentExerciseIndex, 0, 100000) || !integer(set.currentSet, 1, 100000) || set.timed !== false || set.done !== false
    || !weight(set.weightKg) || !integer(set.reps, 0, 100000)) return fail();
  const result: FitnessCurrentSetEditEnvelope = { version: 1, owner, observedAt: input.observedAt, status, set: {
    runtimeId: set.runtimeId, revision: set.revision, paused: set.paused, busy: set.busy, phase: 'execution', workoutName: set.workoutName as string | null,
    exerciseName: set.exerciseName, currentExerciseIndex: set.currentExerciseIndex, currentSet: set.currentSet, timed: false, done: false, weightKg: set.weightKg, reps: set.reps,
  } };
  if (new TextEncoder().encode(JSON.stringify(result)).length > 4096) return fail();
  return result;
}

/** Schema validation is not execution authority. Equal values remain a separate explicit refusal. */
export function canonicalFitnessCurrentSetEditArgs(input: unknown, owner: string, now: Date = new Date()): FitnessCurrentSetEditArgs {
  if (!object(input) || !dataKeys(input, ['action', 'snapshot', 'changes']) || input.action !== 'edit_current_set') return fail();
  const snapshot = canonicalFitnessCurrentSetEditEnvelope(input.snapshot, owner, now);
  if (snapshot.status !== 'available' || !snapshot.set || snapshot.set.busy) return fail();
  return { action: 'edit_current_set', snapshot, changes: canonicalFitnessCurrentSetEditChanges(input.changes) };
}
